import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowClockwise, ArrowSquareOut, CopySimple, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import Layout, { PageHeader, StatusPill, Surface } from "@/components/Layout";
import TablePagination from "@/components/TablePagination";
import { Field, Modal } from "@/pages/Merchants";
import { ADMIN } from "@/lib/routes";
import { apiErrorMessage } from "@/lib/apiErrors";
import {
  addressUrl,
  fetchCryptoDeposits,
  fetchCryptoStats,
  fetchCryptoWallets,
  inspectCryptoWallet,
  manualCryptoSweep,
  reconcileCrypto,
  retryCryptoDeposit,
  syncCryptoWebhooks,
  txUrl,
} from "@/lib/crypto";

const PAGE_SIZE = 25;
const fmtNum = (v, digits = 2) =>
  Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
const fmtUsdt = (v) => `${fmtNum(v)} USDT`;
const fmtBnb = (v) => `${fmtNum(v, 4)} BNB`;

const short = (s, head = 8, tail = 6) => {
  if (!s) return "—";
  const v = String(s);
  if (v.length <= head + tail + 1) return v;
  return `${v.slice(0, head)}…${v.slice(-tail)}`;
};

async function copyText(label, value) {
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Could not copy");
  }
}

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "deposits", label: "Deposits" },
  { id: "wallets", label: "Wallets" },
  { id: "tools", label: "Recovery tools" },
];

function StatCard({ label, value, hint, warn }) {
  return (
    <div className={`bg-white border rounded-sm p-5 ${warn ? "border-amber-300" : "border-zinc-200"}`}>
      <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">{label}</div>
      <div className="mt-2 font-display text-2xl tracking-tight tabular-nums">{value}</div>
      {hint ? <div className={`text-xs mt-1 ${warn ? "text-amber-700" : "text-zinc-500"}`}>{hint}</div> : null}
    </div>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex flex-wrap gap-1 p-1 bg-zinc-100 rounded-sm border border-zinc-200">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={`h-8 px-3 text-sm rounded-sm transition-colors ${
            value === o.id ? "bg-white text-zinc-950 shadow-sm border border-zinc-200" : "text-zinc-600 hover:text-zinc-900"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function CryptoTreasury() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  const network = stats?.network || "mainnet";

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      setStats(await fetchCryptoStats());
    } catch (err) {
      setStatsError(apiErrorMessage(err, "Could not load treasury stats"));
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="crypto-treasury-page">
        <PageHeader
          title="Crypto treasury"
          subtitle="BSC BEP-20 USDT deposits, wallets, and recovery operations"
          actions={(
            <button
              type="button"
              onClick={loadStats}
              className="h-9 px-3 inline-flex items-center gap-1.5 text-sm border border-zinc-200 rounded-sm hover:bg-zinc-50"
            >
              <ArrowClockwise size={14} /> Refresh
            </button>
          )}
        />

        <div className="mb-6">
          <Segmented value={tab} onChange={setTab} options={TABS} />
        </div>

        {statsError && tab === "overview" && (
          <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-100 rounded-sm px-3 py-2">{statsError}</div>
        )}

        {tab === "overview" && (
          <OverviewTab stats={stats} loading={statsLoading} network={network} onRefresh={loadStats} />
        )}
        {tab === "deposits" && <DepositsTab network={network} />}
        {tab === "wallets" && <WalletsTab network={network} />}
        {tab === "tools" && <ToolsTab network={network} onStatsRefresh={loadStats} />}
      </div>
    </Layout>
  );
}

function OverviewTab({ stats, loading, network, onRefresh }) {
  const [reconcile, setReconcile] = useState(null);
  const [reconciling, setReconciling] = useState(false);

  const runReconcile = async () => {
    setReconciling(true);
    try {
      const data = await reconcileCrypto();
      setReconcile(data);
      toast.success(data.status === "healthy" ? "Reconciliation healthy" : "Discrepancy detected");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Reconcile failed"));
    } finally {
      setReconciling(false);
    }
  };

  if (loading && !stats) {
    return <div className="text-sm text-zinc-500 py-16 text-center">Loading treasury…</div>;
  }
  if (!stats) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
        <StatusPill value={stats.network} />
        <span>{stats.chain}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          label="Treasury USDT"
          value={fmtUsdt(stats.treasury?.usdt_balance)}
          hint={short(stats.treasury?.address, 10, 8)}
        />
        <StatCard
          label="Treasury BNB"
          value={fmtBnb(stats.treasury?.bnb_balance)}
          hint="Gas reserve on treasury wallet"
        />
        <StatCard
          label="Gas sponsor BNB"
          value={fmtBnb(stats.gas_sponsor?.bnb_balance)}
          hint={stats.gas_sponsor?.is_low ? `Below min ${stats.gas_sponsor?.min_threshold}` : `Min ${stats.gas_sponsor?.min_threshold}`}
          warn={!!stats.gas_sponsor?.is_low}
        />
        <StatCard
          label="Pending sweeps"
          value={stats.deposits?.pending_sweeps_count ?? 0}
          hint={`${fmtUsdt(stats.deposits?.total_usdt_swept)} swept total`}
          warn={(stats.deposits?.pending_sweeps_count || 0) > 0}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Wallets" value={stats.wallets?.total ?? 0} hint={`${stats.wallets?.merchants ?? 0} merchant · ${stats.wallets?.users ?? 0} cardholder`} />
        <StatCard label="Deposits recorded" value={stats.deposits?.total_count ?? 0} />
        <Surface className="p-5 flex flex-col justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">Reconciliation</div>
            {reconcile ? (
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex items-center gap-2"><StatusPill value={reconcile.status} /></div>
                <div className="text-zinc-600">On-chain: <span className="font-mono tabular-nums">{fmtUsdt(reconcile.on_chain_treasury_usdt)}</span></div>
                <div className="text-zinc-600">DB: <span className="font-mono tabular-nums">{fmtUsdt(reconcile.total_db_deposits_usdt)}</span></div>
                <div className="text-zinc-600">Δ: <span className="font-mono tabular-nums">{fmtNum(reconcile.discrepancy)}</span></div>
              </div>
            ) : (
              <p className="mt-2 text-sm text-zinc-500">Compare treasury USDT on-chain with confirmed DB deposits.</p>
            )}
          </div>
          <button
            type="button"
            disabled={reconciling}
            onClick={runReconcile}
            className="mt-4 h-9 px-4 text-sm bg-zinc-950 text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50 self-start"
          >
            {reconciling ? "Running…" : "Run reconcile"}
          </button>
        </Surface>
      </div>

      {stats.treasury?.address && (
        <Surface className="p-5">
          <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 mb-2">Treasury address</div>
          <div className="flex flex-wrap items-center gap-2">
            <code className="text-xs font-mono break-all">{stats.treasury.address}</code>
            <button type="button" onClick={() => copyText("Treasury address", stats.treasury.address)} className="h-8 px-2 border border-zinc-200 rounded-sm text-xs inline-flex items-center gap-1 hover:bg-zinc-50">
              <CopySimple size={14} /> Copy
            </button>
            <a href={addressUrl(stats.treasury.address, network)} target="_blank" rel="noreferrer" className="h-8 px-2 border border-zinc-200 rounded-sm text-xs inline-flex items-center gap-1 hover:bg-zinc-50">
              BscScan <ArrowSquareOut size={12} />
            </a>
            <button type="button" onClick={onRefresh} className="ml-auto text-xs text-zinc-500 hover:underline">Refresh balances</button>
          </div>
        </Surface>
      )}
    </div>
  );
}

function DepositsTab({ network }) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (status && status !== "all") params.status = status;
      if (search) params.search = search;
      const data = await fetchCryptoDeposits(params);
      setItems(data.items);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to load deposits"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, status, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [status, search, limit]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2 items-end">
        <label className="text-sm">
          <span className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 mb-1">Status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-9 px-3 border border-zinc-200 rounded-sm text-sm bg-white"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
        </label>
        <label className="text-sm flex-1 min-w-[16rem]">
          <span className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 mb-1">Search</span>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") setSearch(searchInput.trim()); }}
              placeholder="tx hash, address, merchant id…"
              className="h-9 w-full pl-8 pr-3 border border-zinc-200 rounded-sm text-sm font-mono"
            />
          </div>
        </label>
        <button
          type="button"
          onClick={() => setSearch(searchInput.trim())}
          className="h-9 px-4 text-sm bg-zinc-950 text-white rounded-sm hover:bg-zinc-800"
        >
          Apply
        </button>
      </div>

      <Surface>
        <div className="table-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
                <th className="px-5 py-3">When</th>
                <th className="px-5 py-3">Merchant / User</th>
                <th className="px-5 py-3 text-right">Amount</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Deposit tx</th>
                <th className="px-5 py-3">Sweep</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-xs text-zinc-500">Loading…</td></tr>
              )}
              {!loading && items.map((d) => {
                const depHref = txUrl(d.tx_hash, network);
                const sweepHref = txUrl(d.sweep_tx_hash, network);
                return (
                  <tr key={d.id} className="border-b border-zinc-50 align-top">
                    <td className="px-5 py-3 text-xs text-zinc-500 whitespace-nowrap">
                      {(d.created_at || "").toString().slice(0, 19).replace("T", " ")}
                    </td>
                    <td className="px-5 py-3">
                      {d.merchant_id ? (
                        <Link to={ADMIN.merchant(d.merchant_id)} className="font-medium hover:underline font-mono text-xs">{d.merchant_id}</Link>
                      ) : <span className="text-zinc-400">—</span>}
                      {d.user_id ? <div className="text-[10px] text-zinc-500 font-mono mt-0.5">{d.user_id}</div> : null}
                    </td>
                    <td className="px-5 py-3 text-right font-mono tabular-nums">
                      {fmtUsdt(d.net_amount ?? d.amount)}
                    </td>
                    <td className="px-5 py-3"><StatusPill value={d.status} /></td>
                    <td className="px-5 py-3 font-mono text-xs">
                      {depHref ? <a href={depHref} target="_blank" rel="noreferrer" className="hover:underline">{short(d.tx_hash, 10, 6)}</a> : short(d.tx_hash)}
                    </td>
                    <td className="px-5 py-3 font-mono text-xs">
                      {d.swept_to_admin ? (
                        sweepHref ? <a href={sweepHref} target="_blank" rel="noreferrer" className="hover:underline">{short(d.sweep_tx_hash, 10, 6)}</a> : short(d.sweep_tx_hash)
                      ) : <span className="text-amber-700 text-[10px] uppercase font-semibold tracking-wide">Unswept</span>}
                    </td>
                  </tr>
                );
              })}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-xs text-zinc-500">No crypto deposits found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          page={page}
          pages={pages}
          total={total}
          limit={limit}
          loading={loading}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      </Surface>
    </div>
  );
}

function WalletsTab({ network }) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [inspect, setInspect] = useState(null);
  const [inspectLoading, setInspectLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit, type };
      if (search) params.search = search;
      const data = await fetchCryptoWallets(params);
      setItems(data.items);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to load wallets"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, type, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [type, search, limit]);

  const openInspect = async (address) => {
    setInspectLoading(true);
    setInspect({ address });
    try {
      setInspect(await inspectCryptoWallet(address));
    } catch (err) {
      toast.error(apiErrorMessage(err, "Inspect failed"));
      setInspect(null);
    } finally {
      setInspectLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2 items-end">
        <label className="text-sm">
          <span className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 mb-1">Type</span>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="h-9 px-3 border border-zinc-200 rounded-sm text-sm bg-white"
          >
            <option value="all">All</option>
            <option value="merchant">Merchant</option>
            <option value="user">Cardholder</option>
          </select>
        </label>
        <label className="text-sm flex-1 min-w-[16rem]">
          <span className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 mb-1">Search</span>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") setSearch(searchInput.trim()); }}
            placeholder="0x… / mer_… / usr_…"
            className="h-9 w-full px-3 border border-zinc-200 rounded-sm text-sm font-mono"
          />
        </label>
        <button
          type="button"
          onClick={() => setSearch(searchInput.trim())}
          className="h-9 px-4 text-sm bg-zinc-950 text-white rounded-sm hover:bg-zinc-800"
        >
          Apply
        </button>
      </div>

      <Surface>
        <div className="table-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
                <th className="px-5 py-3">Address</th>
                <th className="px-5 py-3">Owner</th>
                <th className="px-5 py-3">Index</th>
                <th className="px-5 py-3 text-right">Deposits</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-xs text-zinc-500">Loading…</td></tr>
              )}
              {!loading && items.map((w) => (
                <tr key={w.id} className="border-b border-zinc-50">
                  <td className="px-5 py-3 font-mono text-xs">
                    <button type="button" onClick={() => openInspect(w.address)} className="hover:underline text-left">
                      {short(w.address, 10, 8)}
                    </button>
                  </td>
                  <td className="px-5 py-3 text-xs">
                    {w.user_id ? (
                      <span className="font-mono text-zinc-600">{w.user_id}</span>
                    ) : w.merchant_id ? (
                      <Link to={ADMIN.merchant(w.merchant_id)} className="font-mono hover:underline">{w.merchant_id}</Link>
                    ) : "—"}
                  </td>
                  <td className="px-5 py-3 font-mono tabular-nums text-xs">{w.derivation_index}</td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums text-xs">
                    {fmtUsdt(w.total_deposits)}
                    <div className="text-[10px] text-zinc-500">{w.tx_count ?? 0} tx</div>
                  </td>
                  <td className="px-5 py-3"><StatusPill value={w.status} /></td>
                  <td className="px-5 py-3 text-right">
                    <button type="button" onClick={() => openInspect(w.address)} className="text-xs font-medium hover:underline">
                      Inspect
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-xs text-zinc-500">No wallets found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          page={page}
          pages={pages}
          total={total}
          limit={limit}
          loading={loading}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      </Surface>

      {(inspect || inspectLoading) && (
        <Modal title="Wallet inspector" onClose={() => setInspect(null)} wide>
          {inspectLoading && !inspect?.on_chain ? (
            <p className="text-sm text-zinc-500">Loading on-chain data…</p>
          ) : inspect ? (
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 mb-1">Address</div>
                <code className="text-xs font-mono break-all">{inspect.address}</code>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">USDT</div>
                  <div className="font-mono tabular-nums mt-1">{fmtNum(inspect.on_chain?.usdt_balance)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">BNB</div>
                  <div className="font-mono tabular-nums mt-1">{fmtNum(inspect.on_chain?.bnb_balance, 4)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">Nonce</div>
                  <div className="font-mono tabular-nums mt-1">{inspect.on_chain?.nonce ?? 0}</div>
                </div>
              </div>
              {inspect.wallet && (
                <div className="text-xs text-zinc-600 space-y-1 border-t border-zinc-100 pt-3">
                  <div>Index: <span className="font-mono">{inspect.wallet.derivation_index}</span></div>
                  <div>Merchant: <span className="font-mono">{inspect.wallet.merchant_id || "—"}</span></div>
                  <div>User: <span className="font-mono">{inspect.wallet.user_id || "—"}</span></div>
                </div>
              )}
              <div className="border-t border-zinc-100 pt-3">
                <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 mb-2">Recent txs</div>
                {(inspect.transactions || []).length === 0 && <p className="text-xs text-zinc-500">None</p>}
                <ul className="space-y-2 max-h-40 overflow-y-auto">
                  {(inspect.transactions || []).map((t) => (
                    <li key={t.id} className="flex justify-between gap-2 text-xs">
                      <span className="font-mono">{short(t.tx_hash, 10, 4)}</span>
                      <StatusPill value={t.status} />
                      <span className="font-mono tabular-nums">{fmtNum(t.amount)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <a
                  href={addressUrl(inspect.address, network)}
                  target="_blank"
                  rel="noreferrer"
                  className="h-9 px-3 inline-flex items-center gap-1 text-sm border border-zinc-200 rounded-sm hover:bg-zinc-50"
                >
                  BscScan <ArrowSquareOut size={12} />
                </a>
                <button
                  type="button"
                  onClick={() => copyText("Address", inspect.address)}
                  className="h-9 px-3 inline-flex items-center gap-1 text-sm border border-zinc-200 rounded-sm hover:bg-zinc-50"
                >
                  <CopySimple size={14} /> Copy
                </button>
                <button
                  type="button"
                  onClick={() => setInspect(null)}
                  className="h-9 px-3 ml-auto text-sm text-zinc-600 hover:underline"
                >
                  Close
                </button>
              </div>
            </div>
          ) : null}
        </Modal>
      )}
    </div>
  );
}

function ToolsTab({ network, onStatsRefresh }) {
  const [sweepAddress, setSweepAddress] = useState("");
  const [creditDeposit, setCreditDeposit] = useState(true);
  const [sweepBusy, setSweepBusy] = useState(false);
  const [retryHash, setRetryHash] = useState("");
  const [retryBusy, setRetryBusy] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);

  const onSweep = async () => {
    if (!sweepAddress.trim()) {
      toast.error("Enter a wallet address");
      return;
    }
    setSweepBusy(true);
    try {
      const data = await manualCryptoSweep(sweepAddress.trim(), creditDeposit);
      toast.success(`Swept ${data.swept_amount ?? "funds"}${data.credited ? " and credited" : ""}`);
      setSweepAddress("");
      onStatsRefresh?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Manual sweep failed"));
    } finally {
      setSweepBusy(false);
    }
  };

  const onRetry = async () => {
    if (!retryHash.trim().startsWith("0x")) {
      toast.error("Enter a valid tx hash");
      return;
    }
    setRetryBusy(true);
    try {
      const data = await retryCryptoDeposit(retryHash.trim());
      toast.success(`Reprocessed ${data.transfers?.length ?? 0} transfer(s)`);
      setRetryHash("");
      onStatsRefresh?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Retry deposit failed"));
    } finally {
      setRetryBusy(false);
    }
  };

  const onSync = async () => {
    setSyncBusy(true);
    try {
      const data = await syncCryptoWebhooks();
      toast.success(`Synced ${data.synced_count ?? 0} addresses to Alchemy`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Webhook sync failed"));
    } finally {
      setSyncBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Surface className="p-5 space-y-4">
        <div>
          <div className="font-display text-lg tracking-tight font-medium">Manual sweep</div>
          <p className="text-sm text-zinc-500 mt-1">
            Emergency recovery for unswept USDT on a deposit wallet. Gas is sponsored automatically.
          </p>
        </div>
        <Field label="Wallet address" value={sweepAddress} onChange={setSweepAddress} testId="manual-sweep-address" />
        <label className="flex items-center gap-2 text-sm text-zinc-700">
          <input type="checkbox" checked={creditDeposit} onChange={(e) => setCreditDeposit(e.target.checked)} />
          Credit merchant / cardholder balance after sweep
        </label>
        <button
          type="button"
          disabled={sweepBusy}
          onClick={onSweep}
          className="h-9 px-4 text-sm bg-zinc-950 text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50"
        >
          {sweepBusy ? "Sweeping…" : "Run manual sweep"}
        </button>
      </Surface>

      <Surface className="p-5 space-y-4">
        <div>
          <div className="font-display text-lg tracking-tight font-medium">Retry deposit</div>
          <p className="text-sm text-zinc-500 mt-1">
            Re-fetch an on-chain USDT transfer by hash and run the deposit pipeline again ({network}).
          </p>
        </div>
        <Field label="Transaction hash" value={retryHash} onChange={setRetryHash} testId="retry-deposit-hash" />
        <button
          type="button"
          disabled={retryBusy}
          onClick={onRetry}
          className="h-9 px-4 text-sm bg-zinc-950 text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50"
        >
          {retryBusy ? "Processing…" : "Reprocess deposit"}
        </button>
      </Surface>

      <Surface className="p-5 space-y-4 lg:col-span-2">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="font-display text-lg tracking-tight font-medium">Sync Alchemy webhooks</div>
            <p className="text-sm text-zinc-500 mt-1">
              Register all active and archived deposit addresses with Alchemy Notify so transfer events are not dropped.
            </p>
          </div>
          <button
            type="button"
            disabled={syncBusy}
            onClick={onSync}
            className="h-9 px-4 text-sm border border-zinc-200 rounded-sm hover:bg-zinc-50 disabled:opacity-50"
          >
            {syncBusy ? "Syncing…" : "Sync webhook addresses"}
          </button>
        </div>
      </Surface>
    </div>
  );
}
